/**
 * update_template Tool - Update an existing test template
 */

import type { Tool } from '@modelcontextprotocol/sdk/types.js';
import type { JobClient } from '../clients/job-client.js';
import type { UpdateTemplateRequest } from '@runhuman/shared';

export interface UpdateTemplateArgs extends UpdateTemplateRequest {
  projectId: string;
  templateId: string;
}

export const updateTemplateToolDefinition: Tool = {
  name: 'update_template',
  description: `Update an existing template's configuration.

Can change any combination of: name, test instructions, URL, output schema, results template, duration, device class, GitHub settings.

Common uses:
- Update instructions: update_template(testDescription: "new instructions...")
- Change URL: update_template(url: "https://new-url.com")
- Adjust duration: update_template(targetDurationMinutes: 15)`,
  inputSchema: {
    type: 'object',
    properties: {
      projectId: {
        type: 'string',
        description: 'Project ID (required).',
      },
      templateId: {
        type: 'string',
        description: 'Template ID to update (required). Get from list_templates.',
      },
      name: { type: 'string', description: 'New template name.' },
      testDescription: { type: 'string', description: 'New test instructions.' },
      url: { type: 'string', description: 'New default URL.' },
      outputSchema: { type: 'object', description: 'New JSON schema for structured results.' },
      resultsTemplate: { type: 'string', description: 'New MDForm template for text reports.' },
      targetDurationMinutes: {
        type: 'number',
        description: 'New expected duration in minutes (1-60).',
        minimum: 1,
        maximum: 60,
      },
      deviceClass: {
        type: 'string',
        enum: ['desktop', 'mobile', 'both'],
        description: 'New device class.',
      },
      githubRepos: { type: 'array', items: { type: 'string' }, description: 'New GitHub repos (format: "owner/repo" each).' },
      githubRepo: { type: 'string', description: 'Single GitHub repo (format: "owner/repo"). Use githubRepos for multiple.' },
      autoCreateGithubIssues: { type: 'boolean', description: 'Auto-create GitHub issues.' },
      autoCreateOnlyTesterSurfaced: {
        type: 'boolean',
        description: 'When auto-creating issues, only file tester-surfaced findings (skip telemetry-only).',
      },
      reopenIssuesOnDuplicate: {
        type: 'boolean',
        description: 'When a duplicate match resolves to a closed issue, reopen it. Defaults to false: closed issues stay closed.',
      },
      commentOnDuplicate: {
        type: 'boolean',
        description: 'Default for jobs from this template: when a finding matches an existing issue, post a cross-reference comment on that issue. Defaults to false: re-runs do not comment on existing issues.',
      },
    },
    required: ['projectId', 'templateId'],
  },
};

export async function handleUpdateTemplate(
  args: UpdateTemplateArgs,
  jobClient: JobClient,
): Promise<{ content: Array<{ type: 'text'; text: string }>; isError?: boolean }> {
  try {
    const { projectId, templateId, ...updateData } = args;
    const template = await jobClient.updateTemplate(projectId, templateId, updateData);

    return {
      content: [{
        type: 'text',
        text: `Template Updated!

Name: ${template.name}
Template ID: ${template.id}
Project: ${template.projectId}${template.url ? `\nDefault URL: ${template.url}` : ''}${template.testDescription ? `\nInstructions: ${template.testDescription.substring(0, 100)}${template.testDescription.length > 100 ? '...' : ''}` : ''}${template.targetDurationMinutes ? `\nDuration: ${template.targetDurationMinutes} minutes` : ''}${template.deviceClass ? `\nDevice: ${template.deviceClass}` : ''}`,
      }],
    };
  } catch (error) {
    const err = error as Error & { status?: number };
    return {
      content: [{
        type: 'text',
        text: `Failed to update template\n\nError: ${err.message}${err.status ? `\nStatus: ${err.status}` : ''}`,
      }],
      isError: true,
    };
  }
}
